import {getSignificanceBasedPrecision, human} from '@github-ui/formatters'
import {ListView, useListViewVariant} from '@github-ui/list-view'
import type {RepositoryItem} from '@github-ui/repos-list-shared'
import {verifiedFetch} from '@github-ui/verified-fetch'
import {Box, Text} from '@primer/react'
import type {ComponentProps} from 'react'

import {CompactListItem, DefaultListItem} from './ReposListItem'
import {SortingDropdown} from './SortingDropdown'

export type ListViewVariant = ComponentProps<typeof ListView>['variant']

export function ReposList({
  compactMode,
  repositoryCount,
  repos,
  sortingItemSelected,
  onSortingItemSelect,
  hasActiveFilters,
}: {
  compactMode: boolean
  repositoryCount: number
  repos: RepositoryItem[]
  sortingItemSelected?: string
  hasActiveFilters: boolean
  onSortingItemSelect: (key: string) => void
}) {
  const shouldCap = hasActiveFilters

  function updateCompactModeSetting(value: boolean) {
    const formData = new FormData()
    formData.set('orgs_repos_compact_mode', value.toString())
    verifiedFetch('/repos/preferences', {method: 'PUT', body: formData})
  }

  const metadata = (
    <ListView.Metadata
      title={
        <Text sx={{fontWeight: 'bold'}}>
          {human(repositoryCount, {
            capping: shouldCap ? 1000 : undefined,
            precision: getSignificanceBasedPrecision(repositoryCount),
          })}{' '}
          {repositoryCount === 1 ? 'repository' : 'repositories'}
        </Text>
      }
      densityToggle={
        <ListView.DensityToggle sx={{display: ['none', 'flex']}} /> // Hide density toggle on small screens
      }
    >
      <SortingDropdown sortingItemSelected={sortingItemSelected || ''} onSortingItemSelect={onSortingItemSelect} />
    </ListView.Metadata>
  )
  return (
    <Box sx={{border: '1px solid', borderColor: 'border.muted', borderRadius: 2}}>
      <ListView
        onVariantChange={value => updateCompactModeSetting(value === 'compact')}
        variant={compactMode ? 'compact' : 'default'}
        metadata={metadata}
        title="Repositories list"
        titleHeaderTag="h2"
      >
        {repos.map(repo => (
          <ReposListItem key={repo.name} repo={repo} />
        ))}
      </ListView>
    </Box>
  )
}

function ReposListItem({repo}: {repo: RepositoryItem}) {
  const {variant} = useListViewVariant()
  if (variant === 'default') {
    return <DefaultListItem repo={repo} />
  }

  return (
    <>
      <CompactListItem repo={repo} sx={{display: ['none', 'flex']}} />
      <DefaultListItem repo={repo} sx={{display: ['block', 'none'], px: 2, pb: 2}} />
    </>
  )
}

try{ ReposList.displayName ||= 'ReposList' } catch {}
try{ ReposListItem.displayName ||= 'ReposListItem' } catch {}